import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AgCharts } from "ag-charts-react";
import { useQuery } from "@tanstack/react-query";

import { personsSummaryMap, salaryAbsencesQuery } from "../../api";
import { customChartThemes } from "../../common/grid";
import { LoadingContainer } from "../../common/loaders/loadingcontainer";
import { localized } from "../../common/utils";


export function SicknessAbsenceDays() {
  const { t } = useTranslation();

  const persons = useQuery(personsSummaryMap).data;
  const { data } = useQuery(salaryAbsencesQuery);

  const [combinedData, setCombinedData] = useState();

  const { chartTheme } = customChartThemes;

  useEffect(() => {
    if (data && persons) {
      const salaryabsences = data.salaryabsences_response.salaryabsences ?? [];
      const last12MonthsData = salaryabsences.filter(item => isActivePerson(item.PERSONNUMBER) && isDateInLast12Months(item.STARTDATE));

      //PAYTYPE values 1, 101, 102, 2, 20
      const sicknessLeavesData = last12MonthsData.filter(item =>
        ["1", "101", "102", "2", "20"].includes(item.ABSENCETYPE)
      );
      //Filter empty data
      const filteredData = sicknessLeavesData.filter(person => person.DAYS > 0);

      //Paid and unpaid absences
      const paidAbsenceData = filteredData.filter(item => item.PAID === 1 || !item.PAID);
      const nonPaidAbsenceData = filteredData.filter(item => item.PAID === 0);

      //Total paid and unpaid absences
      const totalPaidAbsencesPerPerson = calculateTotalAbsencesPerPerson(paidAbsenceData);
      const totalNonPaidAbsencesPerPerson = calculateTotalAbsencesPerPerson(nonPaidAbsenceData);

      //Combine for person
      setCombinedData(totalPaidAbsencesPerPerson.map(paidItem => {
        const nonPaidItem = totalNonPaidAbsencesPerPerson.find(
          nonPaidItem => nonPaidItem.personName === paidItem.personName
        );

        return {
          personName: paidItem.personName,
          totalPaidDays: paidItem.totalDays,
          totalNonPaidDays: nonPaidItem ? nonPaidItem.totalDays : 0,
        };
      }));
    };
  }, [data, persons]);

  function calculateTotalAbsencesPerPerson(data) {
    const totalAbsences = {};

    data.forEach(item => {
      const days = item.DAYS;
      const personName = item.PERSONNAME;

      totalAbsences[personName] = (totalAbsences[personName] || 0) + days;
    });

    const formattedData = Object.keys(totalAbsences).map(personName => ({
      personName: personName,
      totalDays: totalAbsences[personName],
    }));
    return formattedData;
  }

  function isActivePerson(personNumber) {
    const person = persons[personNumber];
    return person ? !person.ended : false;
  }

  function isDateInLast12Months(dateString) {
    const currentDate = new Date();
    const date = new Date(dateString.replace(/(\d{2}).(\d{2}).(\d{4})/, "$3-$2-$1"));
    const last12Months = new Date(currentDate.setMonth(currentDate.getMonth() - 12));

    return date >= last12Months;
  }

  /** @type {import("ag-grid-enterprise").AgChartOptions} */
  const options = {
    data: combinedData,
    series: [
      {
        type: "bar",
        direction: "horizontal",
        xKey: "personName",
        yKey: "totalPaidDays",
        yName: t("WithPay", "Palkallinen"),
      },
      {
        type: "bar",
        direction: "horizontal",
        xKey: "personName",
        yKey: "totalNonPaidDays",
        yName: t("WithoutPay", "Palkaton"),
      },
    ],
    theme: { ...chartTheme, palette: { ...chartTheme.palette, fills: ["#B7A0E0", "#FFC107"] } },
    legend: {
      enabled: true,
    },
    overlays: {
      noData: { text: `${t("No", "Ei")} ${t("data", "tietoja")}` },
    },
    locale: localized.agChartsLocale,
  };

  return combinedData ? (
    <>
      <h4 style={{ color: "var(--heeros-blue)" }}>{t("SicknessAbsenceDays", "Sairauspoissaolopäivät")}</h4>

      <div>
        <AgCharts options={options} />
      </div>
    </>
  ) : (
    <LoadingContainer />
  );
}
